<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Add -->
            <div class="col-12 col-lg-3 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search cached domains..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-sm text-light"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap; background: #00A0C1;"
                  @click="openAddModal()">
                  <i class="fa-solid fa-plus me-1"></i> Cache Domain
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Filter:</span>
                  <select v-model="filterStatusBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="f in filterStatus" :value="f.value" :key="f.value">{{ f.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" style="max-width: 260px;">Domain</th>
                     <th scope="col" style="width: 100px;">Status</th>
                     <th scope="col" style="width: 130px;">Login Rule</th>
                     <th scope="col" style="width: 120px;">Unsafe Code</th>
                     <th scope="col" style="width: 160px;">Expiry</th>
                     <th scope="col" style="width: 160px;">Created</th>
                     <th scope="col" style="width: 100px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="7">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(cd, index) in cachedDomains" :key="index">
                     <td style="font-family: monospace; font-size: 12px;">{{ cd.domain }}</td>
                     <td>
                        <span class="status-pill" :class="(cd.is_safe == 0 || cd.is_safe == 3) ? 'status-unsafe' : 'status-safe'">
                           <i :class="(cd.is_safe == 0 || cd.is_safe == 3) ? 'fa-solid fa-circle-xmark' : 'fa-solid fa-circle-check'" class="me-1"></i>
                           {{ (cd.is_safe == 0 || cd.is_safe == 3) ? 'Unsafe' : 'Safe' }}
                        </span>
                     </td>
                     <td>
                        <span class="status-pill" :class="getLoginRuleClass(cd.login_rule)">
                           <i :class="getLoginRuleIcon(cd.login_rule)" class="me-1"></i>
                           {{ getLoginRuleLabel(cd.login_rule) }}
                        </span>
                     </td>
                     <td>
                        <span v-if="cd.unsafe_code" class="code-badge">{{ cd.unsafe_code }}</span>
                        <span v-else class="text-muted">—</span>
                     </td>
                     <td class="date-text">{{ formatDate(cd.expiry_date) }}</td>
                     <td class="date-text">{{ formatDate(cd.date_created) }}</td>
                     <td>
                        <div class="d-flex gap-2">
                           <button type="button" class="ms-action-btn ms-action-btn--edit"
                              title="Edit" @click="openEditModal(cd)">
                              <i class="fa-solid fa-pen"></i>
                           </button>
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                              title="Delete" @click="confirmDelete(cd)">
                              <i class="fa-solid fa-xmark"></i>
                           </button>
                        </div>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>

   <!-- Add / Edit Modal -->
   <div class="modal-backdrop-custom" v-if="showFormModal" @click.self="closeFormModal()">
      <div class="modal-content-custom">
         <button @click="closeFormModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-database me-2" style="color: #00A0C1;"></i>
            {{ editId ? 'Edit Cached Domain' : 'Add Cache Domain' }}
         </h5>
         <hr>
         <div class="modal-body-custom">
            <label class="filter-label">Domain / IP Address</label>
            <input type="text" class="form-control form-control-sm mb-3"
               style="border-radius: 8px;" placeholder="e.g. example.com or 1.2.3.4" v-model="inputWebsite">

            <label class="filter-label">Status</label>
            <div class="d-flex gap-2 mb-3">
               <button type="button" class="btn btn-sm flex-fill"
                  :class="inputStatus === 1 ? 'status-btn-active-safe' : 'status-btn-inactive'"
                  @click="inputStatus = 1">
                  <i class="fa-solid fa-circle-check me-1"></i> Safe
               </button>
               <button type="button" class="btn btn-sm flex-fill"
                  :class="inputStatus === 0 ? 'status-btn-active-unsafe' : 'status-btn-inactive'"
                  @click="inputStatus = 0">
                  <i class="fa-solid fa-circle-xmark me-1"></i> Unsafe
               </button>
            </div>

            <label class="filter-label">Login Rule</label>
            <select class="form-select form-select-sm mb-3" style="border-radius: 8px;" v-model="inputLoginRule">
               <option :value="0">0 - Pending / Unevaluated</option>
               <option :value="1">1 - Safe Login Domain</option>
               <option :value="2">2 - Dangerous / Flagged</option>
            </select>

            <label class="filter-label">Unsafe Code</label>
            <select class="form-select form-select-sm mb-3" style="border-radius: 8px;" v-model="inputCode">
               <option v-for="(val, key) in codeOptions" :key="key" :value="val">{{ key }}</option>
            </select>

            <label class="filter-label">Expiry Date</label>
            <input type="date" class="form-control form-control-sm"
               style="border-radius: 8px;" :min="today" max="2100-12-31"
               v-model="inputExpiry" @keydown="preventKeyPress($event)">
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="closeFormModal()">Cancel</button>
            <button v-if="!editId" type="button" class="btn btn-sm text-light" style="background: #00A0C1; border-radius: 8px;"
               @click="addCacheDomain()">
               <i class="fa-solid fa-plus me-1"></i> Add
            </button>
            <button v-else type="button" class="btn btn-sm text-light" style="background: #00A0C1; border-radius: 8px;"
               @click="editCacheDomain()">
               <i class="fa-solid fa-floppy-disk me-1"></i> Save
            </button>
         </div>
      </div>
   </div>

   <!-- Delete Confirm Modal -->
   <div class="modal-backdrop-custom" v-if="showDeleteModal" @click.self="showDeleteModal = false">
      <div class="modal-content-custom">
         <button @click="showDeleteModal = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-xmark me-2" style="color: #c62828;"></i>
            Delete Cached Domain
         </h5>
         <hr>
         <div class="modal-body-custom">
            <p>Are you sure you want to delete this cached domain?</p>
            <div class="site-display">{{ deleteDomainName }}</div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showDeleteModal = false">Cancel</button>
            <button type="button" class="btn btn-sm text-light" style="background: #c62828; border-radius: 8px;"
               @click="deleteCacheDomain()">
               <i class="fa-solid fa-xmark me-1"></i> Delete
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         showFormModal: false,
         showDeleteModal: false,
         deleteID: 0,
         deleteDomainName: '',
         editId: null,
         search: "",
         cachedDomains: [],
         inputWebsite: "",
         inputStatus: 0,
         inputLoginRule: 0,
         inputCode: "",
         inputExpiry: null,
         today: new Date().toISOString().split('T')[0],
         debounceFn: null,
         paginationData: {},
         statusOptions: { "Safe": 1, "Unsafe": 0 },
         codeOptions: {
            "None": "",
            "0 - No Code (Unsafe)": 0,
            "346 - Block HTTP": 346,
            "347 - Site Age less than 4 months": 347,
            "348 - Domain unsafe on google": 348,
            "349 - Domain unsafe on webshrinker": 349,
            "350 - Unsafe domain": 350,
            "351 - Block all file sharing": 351,
            "352 - URL contain email address": 352,
            "353 - Domain is longer than 50 characters": 353,
            "354 - Block chat messaging": 354,
            "355 - Block webshrinker category": 355,
            "356 - Website exist in unsafe website table": 356,
            "357 - Block all websites except allowed websites": 357,
            "358 - Info tld and contain hyphen": 358,
            "359 - Unsafe site for 6 months": 359,
            "360 - Block oauth site": 360,
            "315 - Blocked from block list": 315,
         },
         filterStatus: [
            { text: "Safe", value: 1 },
            { text: "Unsafe", value: 0 },
            { text: "Both", value: 2 },
         ],
         filterStatusBy: 2,
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
         ],
         sortBy: "date_created__desc",
         sorting: { field: "date_created", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
      filterByStatus() { return this.filterStatusBy },
   },

   watch: {
      currentPage() { this.getCachedDomains() },
      filterByStatus() { this.getCachedDomains() },
      rowsPerPage() { this.pagination.page = 1; this.getCachedDomains() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getCachedDomains()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getCachedDomains(), 500)
      this.getCachedDomains()
   },

   methods: {
      formatDate(date) { return moment(date).format("DD-MMM-YYYY, hh:mm A") },

      getLoginRuleClass(rule) {
         const val = Number(rule)
         if (val === 1) return 'status-safe'
         if (val === 2) return 'status-unsafe'
         return 'status-pending'
      },

      getLoginRuleIcon(rule) {
         const val = Number(rule)
         if (val === 1) return 'fa-solid fa-shield-check'
         if (val === 2) return 'fa-solid fa-triangle-exclamation'
         return 'fa-solid fa-clock'
      },

      getLoginRuleLabel(rule) {
         const val = Number(rule)
         if (val === 1) return 'Safe (1)'
         if (val === 2) return 'Unsafe (2)'
         return 'Pending (0)'
      },

      isValidDomain(input) {
         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
         const ipPattern = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
         return domainPattern.test(input) || ipPattern.test(input);
      },

      preventKeyPress(evt) { evt.preventDefault(); },

      resetValues() {
         this.inputWebsite = ""; this.inputStatus = 0;
         this.inputLoginRule = 0;
         this.inputCode = ""; this.inputExpiry = null; this.editId = null;
      },

      openAddModal() {
         this.resetValues()
         this.showFormModal = true
      },

      openEditModal(cd) {
         this.inputWebsite = cd.domain
         this.inputStatus = cd.is_safe
         this.inputLoginRule = cd.login_rule !== undefined && cd.login_rule !== null ? Number(cd.login_rule) : 0
         this.inputCode = cd.unsafe_code
         this.inputExpiry = moment(cd.expiry_date).format("YYYY-MM-DD")
         this.editId = cd.id
         this.showFormModal = true
      },

      closeFormModal() {
         this.showFormModal = false
         this.resetValues()
      },

      confirmDelete(cd) {
         this.deleteID = cd.id
         this.deleteDomainName = cd.domain
         this.showDeleteModal = true
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async addCacheDomain() {
         try {
            if (!this.inputWebsite || !this.isValidDomain(this.inputWebsite)) {
               this.$toast.error("Please provide a valid domain or IP address.")
               return
            }
            const payload = {
               domain: this.inputWebsite,
               is_safe: this.inputStatus,
               login_rule: this.inputLoginRule,
               unsafe_code: this.inputCode,
               expiry_date: this.inputExpiry,
            }
            const resp = await this.$axios.post("admin-controls/create-cache-domain", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getCachedDomains()
            }
            this.closeFormModal()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add cached domain."))
            this.closeFormModal()
         }
      },

      async editCacheDomain() {
         try {
            if (!this.inputWebsite || !this.isValidDomain(this.inputWebsite)) {
               this.$toast.error("Please provide a valid domain or IP address.")
               return
            }
            const payload = {
               id: this.editId,
               domain: this.inputWebsite,
               is_safe: this.inputStatus,
               login_rule: this.inputLoginRule,
               unsafe_code: this.inputCode,
               expiry_date: this.inputExpiry,
            }
            const resp = await this.$axios.post("admin-controls/update-cached-domain", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getCachedDomains()
            }
            this.closeFormModal()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to update cached domain."))
            this.closeFormModal()
         }
      },

      async deleteCacheDomain() {
         try {
            const resp = await this.$axios.delete("admin-controls/remove-cache-domain/" + this.deleteID)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getCachedDomains()
            }
            this.deleteID = 0; this.deleteDomainName = '';
            this.showDeleteModal = false
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove cached domain."))
            this.deleteID = 0; this.showDeleteModal = false
         }
      },

      async getCachedDomains() {
         let apiPath = "admin-controls/get-cached-domains?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&status=" + this.filterStatusBy

         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&domain=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.cached_domains === "No Record Found")
                  this.cachedDomains = []
               else {
                  this.cachedDomains = response.data.cached_domains
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load cached domains."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.date-text { font-size: 13px; color: #6c757d; white-space: nowrap; }

/* Status pills */
.status-pill {
   padding: 4px 10px; border-radius: 6px;
   font-size: 11px; font-weight: 600;
   display: inline-flex; align-items: center; white-space: nowrap;
}
.status-safe { background: #e6f9ee; color: #00873c; }
.status-unsafe { background: #fde8ea; color: #c62828; }
.status-pending { background: #fff8e1; color: #b78103; }

/* Unsafe code badge */
.code-badge {
   font-family: monospace; font-size: 11px;
   background: #f1f3f5; color: #455a64;
   padding: 3px 8px; border-radius: 4px;
   font-weight: 600;
}

/* Action buttons */
.btn-action {
   width: 30px; height: 30px; padding: 0;
   border-radius: 6px; display: inline-flex;
   align-items: center; justify-content: center;
   font-size: 12px; transition: all 0.15s ease;
}
.btn-action-edit { border: 1px solid #00A0C1; color: #00A0C1; background: #fff; }
.btn-action-edit:hover { background: #00A0C1; color: #fff; }
.btn-action-delete { border: 1px solid #c62828; color: #c62828; background: #fff; }
.btn-action-delete:hover { background: #c62828; color: #fff; }

/* Status toggle buttons in modal */
.status-btn-active-safe { background: #e6f9ee; color: #00873c; border: 2px solid #00873c; border-radius: 8px; font-weight: 600; }
.status-btn-active-unsafe { background: #fde8ea; color: #c62828; border: 2px solid #c62828; border-radius: 8px; font-weight: 600; }
.status-btn-inactive { background: #f8f9fa; color: #6c757d; border: 1px solid #dee2e6; border-radius: 8px; font-weight: 500; }
.status-btn-inactive:hover { background: #e9ecef; }

/* Site display in delete modal */
.site-display {
   font-family: monospace; font-size: 13px;
   background: #fde8ea; padding: 8px 12px;
   border-radius: 8px; color: #c62828; word-break: break-all;
}

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 500px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; display: block;
}
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }
</style>